import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, ArrowLeft } from "lucide-react";
import { motion } from "framer-motion";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// 커플 발표일 포맷: "2026-06-06" → "2026.6.6"
function fmtCoupleDate(s) {
  if (!s) return "";
  const m = String(s).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[1]}.${parseInt(m[2], 10)}.${parseInt(m[3], 10)}`;
  return s;
}

export default function LizCouplesPage() {
  const navigate = useNavigate();
  const [couples, setCouples] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/liz-couples`)
      .then(r => r.ok ? r.json() : { couples: [] })
      .then(d => setCouples((d.couples || []).filter(c => c.is_visible !== false)))
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-stone-50 pt-20">
      {/* Header */}
      <div className="bg-gradient-to-r from-burgundy-600 to-burgundy-700 py-10">
        <div className="max-w-6xl mx-auto px-6">
          <button onClick={() => navigate("/couple-board")} className="flex items-center gap-1.5 text-white/70 hover:text-white text-sm mb-4 transition-colors">
            <ArrowLeft className="w-4 h-4" /> 커플 게시판
          </button>
          <div className="flex items-center gap-3">
            <Heart className="w-7 h-7 text-pink-300 fill-pink-300" />
            <div>
              <h1 className="text-3xl font-bold text-white">리즈커플들</h1>
              <p className="text-burgundy-200 mt-1">New Coupling에서 만난 소중한 인연들</p>
            </div>
          </div>
        </div>
      </div>

      {/* Couples Grid - 5 columns */}
      <div className="max-w-6xl mx-auto px-6 py-8">
        {isLoading ? (
          <div className="text-center py-16 text-stone-400">로딩중...</div>
        ) : couples.length === 0 ? (
          <div className="text-center py-16">
            <Heart className="w-12 h-12 text-stone-300 mx-auto mb-4" />
            <p className="text-stone-400">아직 등록된 커플이 없습니다</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {couples.map((couple, idx) => {
              const mYear = couple.male_birth_year ? String(couple.male_birth_year).slice(2) : "";
              const fYear = couple.female_birth_year ? String(couple.female_birth_year).slice(2) : "";
              return (
                <motion.div
                  key={couple.id || couple.couple_no}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="bg-white rounded-2xl border border-stone-200 overflow-hidden hover:shadow-lg hover:border-pink-300 transition-all group"
                  data-testid={`liz-couple-${couple.couple_no}`}
                >
                  {/* Photo */}
                  <div className="relative aspect-square bg-stone-100">
                    {couple.photo_url ? (
                      <img src={couple.photo_url} alt={`${couple.couple_no}호 커플`} className="w-full h-full object-cover" style={{ objectPosition: couple.photo_position === "top" ? "center top" : couple.photo_position === "bottom" ? "center bottom" : "center" }} />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-pink-50 to-burgundy-50">
                        <Heart className="w-10 h-10 text-pink-300" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                    <span className="absolute top-2 left-2 bg-burgundy-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">
                      {couple.couple_no}호
                    </span>
                    {couple.announced_date && (
                      <span className="absolute top-2 right-2 bg-white/90 text-burgundy-700 text-[10.5px] font-bold px-2 py-1 rounded-full shadow" data-testid={`liz-couple-date-${couple.couple_no}`}>
                        {fmtCoupleDate(couple.announced_date)}
                      </span>
                    )}
                  </div>

                  {/* Info */}
                  <div className="p-3 text-center">
                    <p className="text-sm font-bold text-stone-800">
                      {couple.couple_no}호 커플
                    </p>
                    <p className="text-xs text-stone-500 mt-1">
                      {couple.male_name}OO ({couple.male_location}{mYear ? `, ${mYear}` : ""})
                    </p>
                    <p className="text-xs text-stone-500">
                      {couple.female_name}OO ({couple.female_location}{fYear ? `, ${fYear}` : ""})
                    </p>
                    {couple.message && (
                      <p className="text-[11px] text-pink-500 mt-1.5 italic">"{couple.message}"</p>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
